import React from "react";
import {
  Grid,
  Box,
  Switch,
  Button,
  TextField,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Formik } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MuiAlert from "@mui/material/Alert";
import { ModernLiquidTable, LiquidIconButton } from "../../../components/modern-table";
import { Portlet, PortletBody } from "../../../partials/content/Portlet";
import {
  createUrl,
  getUrlsByUser,
  removeUrl,
  togglePublishUrl,
} from "../../../api/shorturl";

export default function ShorturlPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [failure, setFailure] = React.useState(false);
  const [error, setError] = React.useState("Link Creation Failed!");

  const handleCloseSuccess = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSuccess(false);
  };
  const handleCloseFailure = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setFailure(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setError("Link copied to clipboard!");
      setSuccess(true);
    } catch (err) {
      setError("Failed to copy link");
      setFailure(true);
    }
  };

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUrlsByUser();
      if (response && response.data && response.data.urls) {
        setRows(response.data.urls);
      }
    } catch (error) {
      console.error("Failed to fetch URLs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const togglePublished = async (id) => {
    try {
      await togglePublishUrl(id);
      await fetchData(); // Refresh data
      setError("Status berjaya dikemaskini!");
      setSuccess(true);
    } catch (error) {
      setError(error.message || "Ralat semasa mengemaskini status");
      setFailure(true);
    }
  };

  const deleteUrl = async (id) => {
    try {
      await removeUrl(id);
      await fetchData(); // Refresh data
      setError("Pautan berjaya dipadam!");
      setSuccess(true);
    } catch (error) {
      setError(error.message || "Ralat semasa memadam pautan");
      setFailure(true);
    }
  };

  const columns = [
    {
      field: "_rowNumber",
      title: "No",
      width: 75,
      render: (rowData) => <p>{rowData._rowNumber}</p>,
    },
    {
      field: "shorten",
      title: "Pautan Pendek",
      width: 200,
      render: (rowData) => {
        const fullUrl = `https://sigmagema.com/r/${rowData.shorten}`;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#667eea', textDecoration: 'none' }}
            >
              {rowData.shorten}
            </a>
            <Tooltip title="Salin pautan">
              <LiquidIconButton size="small" onClick={() => copyToClipboard(fullUrl)}>
                <ContentCopyIcon fontSize="small" />
              </LiquidIconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "original",
      title: "Pautan Asal",
      width: 350,
      render: (rowData) => (
        <Tooltip title={rowData.original}>
          <Box
            sx={{
              maxWidth: "350px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {rowData.original}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "visited",
      title: "Lawatan",
      width: 100,
      render: (rowData) => <p>{rowData.visited || 0}</p>,
    },
    {
      field: "published",
      title: "Status",
      width: 100,
      render: (rowData) => (
        <Switch
          checked={rowData.published}
          onChange={() => togglePublished(rowData.id)}
          color="primary"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#667eea',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#667eea',
            },
          }}
        />
      ),
    },
    {
      field: "createdAt",
      title: "Tarikh Dibuat",
      width: 120,
      render: (rowData) => {
        if (rowData.createdAt) {
          return new Date(rowData.createdAt).toLocaleDateString("ms-MY");
        }
        return "-";
      },
    },
    {
      field: "actions",
      title: "Tindakan",
      width: 80,
      render: (rowData) => (
        <Tooltip title="Padam">
          <LiquidIconButton
            size="small"
            onClick={() => {
              if (
                window.confirm("Adakah anda pasti untuk memadam pautan ini?")
              ) {
                deleteUrl(rowData.id);
              }
            }}
          >
            <DeleteIcon />
          </LiquidIconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Portlet>
        <PortletBody fluid={true}>
          <Formik
            initialValues={{
              original: "",
              shorten: "",
            }}
            validate={async (values) => {
              const errors = {};

              if (!values.original) {
                errors.original = "Required";
              } else if (!/^https?:\/\/.+/.test(values.original)) {
                errors.original = "Must be a valid URL";
              }

              if (!values.shorten) {
                errors.shorten = "Required";
              } else if (!/^[a-zA-Z0-9-_]+$/.test(values.shorten)) {
                errors.shorten =
                  "Only alphanumeric characters, hyphens and underscores allowed";
              } else if (values.shorten.length < 3) {
                errors.shorten = "Must be at least 3 characters";
              }

              return errors;
            }}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                await createUrl({
                  original: values.original,
                  shorten: values.shorten,
                  published: true,
                });
                setError("Pautan berjaya dibuat!");
                setSuccess(true);
                resetForm();
                await fetchData(); // Refresh table data
              } catch (err) {
                setError(err.message || "Gagal membuat pautan");
                setFailure(true);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form sx={{ width: "100%" }} onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-3 col-sm-12">
                    <TextField
                      label="Pautan Pendek"
                      margin="normal"
                      type="text"
                      name="shorten"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.shorten}
                      variant="outlined"
                      fullWidth
                      required
                      error={touched.shorten && Boolean(errors.shorten)}
                      helperText={touched.shorten && errors.shorten}
                      placeholder="e.g. event2024"
                    />
                  </div>
                  <div className="col-md-6 col-sm-12">
                    <TextField
                      label="Pautan Asal"
                      margin="normal"
                      type="url"
                      name="original"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.original}
                      variant="outlined"
                      fullWidth
                      required
                      error={touched.original && Boolean(errors.original)}
                      helperText={touched.original && errors.original}
                      placeholder="https://example.com/very-long-url"
                    />
                  </div>
                  <div className="col-md-3 col-sm-12">
                    {!isSubmitting ? (
                      <Button
                        type="submit"
                        variant="contained"
                        className="btn btn-lg btn-label-brand mt-5"
                        disabled={Object.keys(errors).length > 0}
                      >
                        Tambah Pautan
                      </Button>
                    ) : (
                      <CircularProgress className="mt-5" />
                    )}
                  </div>
                </div>
              </form>
            )}
          </Formik>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={success}
            autoHideDuration={6000}
            onClose={handleCloseSuccess}
          >
            <MuiAlert onClose={handleCloseSuccess} severity="success">
              Link Successfully Created!
            </MuiAlert>
          </Snackbar>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={failure}
            autoHideDuration={6000}
            onClose={handleCloseFailure}
          >
            <MuiAlert onClose={handleCloseFailure} severity="error">
              {error}
            </MuiAlert>
          </Snackbar>
        </PortletBody>
      </Portlet>
      <Portlet>
        <PortletBody fluid={true}>
          <Grid className="p-3" container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <span className="kt-section__sub">
                  Senarai pautan yang telah dipendekkan
                </span>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <ModernLiquidTable
                columns={columns}
                data={rows}
                title={`Bilangan pautan: ${rows.length}`}
                loading={loading}
                onRefresh={fetchData}
                emptyStateMessage="Tiada pautan untuk dipaparkan"
                options={{
                  selection: false,
                  pageSize: 10,
                  pageSizeOptions: [5, 10, 20, 50, 100],
                }}
              />
            </Grid>
          </Grid>
        </PortletBody>
      </Portlet>
    </>
  );
}
